import React, { useEffect, useState } from 'react';
import { IconWhatsapp, Spinner } from 'takeat-design-system-ui-kit';
import { useAuth } from '~/context/AuthContext';
import useWhatsappStore from '~/store/whatsappStore';

import { HeaderButton } from './styles';
import WhatsappSheet from '../whatsappSheet';

const COUNTDOWN_TIME = 120;

const WhatsappButton = () => {
  const [shouldRender, setShouldRender] = useState(false);
  const { getUser } = useAuth();
  const user = getUser() || {};
  const [countdown, setCountdown] = useState(COUNTDOWN_TIME);
  const [sheetOpen, setSheetOpen] = useState(false);

  const isValidQrCode = (qrcode) => {
    if (!qrcode) return false;
    if (typeof qrcode === 'string' && qrcode.trim() !== '') return true;
    if (typeof qrcode === 'object' && Object.keys(qrcode).length === 0)
      return false;
    return true;
  };

  const {
    wppConnected,
    wppStatus,
    wppQrcode,
    showReconnect,
    setShowReconnect,
    loadingQrCode,
    createInstance,
    logoutWpp,
    wppApiAvailable,
    waitingForWebhook,
    phoneDetails,
    setWppQrcode,
    fetchInstanceStatus,
    fetchRestaurantInfo,
    wppStatusChecked,
  } = useWhatsappStore();

  useEffect(() => {
    if (showReconnect) {
      setSheetOpen(true);
      setShowReconnect(false);
    }
  }, [showReconnect, setShowReconnect]);

  useEffect(() => {
    let timerId;

    if (isValidQrCode(wppQrcode)) {
      setCountdown(COUNTDOWN_TIME);
      timerId = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(timerId);
            setWppQrcode(null);
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerId) {
        clearInterval(timerId);
      }
    };
  }, [wppQrcode, setWppQrcode]);

  const handleToggleReconnect = (e) => {
    setSheetOpen((s) => !s);
    e.stopPropagation();
  };

  useEffect(() => {
    if (user.use_wpp) {
      // Check if instance exists first
      fetchInstanceStatus().then((instanceStatusResult) => {
        // If instance doesn't exist, we already set the state in fetchInstanceStatus
        if (!instanceStatusResult.success && instanceStatusResult.noInstance) {
          return;
        }

        // If instance exists or any other status, fetch restaurant info
        fetchRestaurantInfo();
      });
    }
  }, [user.use_wpp, fetchInstanceStatus, fetchRestaurantInfo]);

  useEffect(() => {
    const newShouldRender = user.use_wpp && user.access !== 'pdv';
    setShouldRender(newShouldRender);
  }, [user.use_wpp, user.access]);

  if (!shouldRender) return null;

  const headerColor = () => {
    if (!wppApiAvailable) return undefined;
    if (wppStatus === 'open') return 'green';
    if (wppStatus === 'created') return 'yellow';
    return 'red';
  };

  const iconColor = () => {
    if (!wppApiAvailable) return '#97928F';
    if (wppStatus === 'open') return '#2ec9b7';
    if (wppStatus === 'created') return '#fbc658';
    return '#ff2c3a';
  };

  const isLoading = !wppStatusChecked && !wppApiAvailable;

  const labelText = () => {
    if (isLoading) return null;
    if (!wppApiAvailable) return 'Status desconhecido';
    if (wppStatus === 'open') return 'Conectado';
    if (wppStatus === 'created') return 'Conectando...';
    return 'Desconectado';
  };

  return (
    <HeaderButton color={headerColor()}>
      <div style={{ position: 'relative' }}>
        <div
          onClick={handleToggleReconnect}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleToggleReconnect(e);
            }
          }}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          {isLoading ? (
            <Spinner size={22} />
          ) : (
            <IconWhatsapp size={22} color={iconColor()} />
          )}
          <span>{labelText()}</span>
        </div>
        <WhatsappSheet
          open={sheetOpen}
          onClose={() => setSheetOpen(false)}
          isValidQrCode={isValidQrCode}
          countdown={countdown}
          COUNTDOWN_TIME={COUNTDOWN_TIME}
          user={user}
          onRefresh={() => {
            fetchInstanceStatus().then((instanceStatusResult) => {
              if (
                !instanceStatusResult.success &&
                instanceStatusResult.noInstance
              ) {
                return;
              }
              fetchRestaurantInfo();
            });
          }}
        />
      </div>
    </HeaderButton>
  );
};

export default WhatsappButton;
